import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { Sizes } from '@ratehub/base-ui';

import HeadingPropTypes from '../definitions/HeadingPropTypes';
import HeadingBlock from './HeadingBlock';


function StoreFrontContainer({
    heading,
    mainTitle,
    variant = STORE_FRONT_VARIANTS.standard,
    children,
    ...otherProps
}) {
    // support both heading object or standalone heading props until FIT-1272
    const headingBlockProps = heading
        ? heading
        : { title: mainTitle };
    const hasAnyHeading = mainTitle || heading?.mainTitle;

    const childrenCount = React.Children.count(children);
    const columnCount = childrenCount <= MAX_TILES_PER_ROW_DESKTOP
        ? childrenCount
        : Math.ceil(childrenCount / 2);  // split children across 2 rows (isStandard)

    // • would love to be able to calculate `childrenCount mod columnCount`, but `columnCount` is not
    //   the last word on the grid’s columns: container size enters into it too
    // • as a result, for `isCondensed`, the last row of items will not be centred, except for when
    //   it is a list of two columns of StoreFrontItems, and there are exactly 3 items
    // • aligning the last row to the left matches how `isStandard` always works when `hasAnyHeading` is true

    // turn variant into booleans for easier logic; only two so far, and hoping it stays that way 🤞
    const isStandard = variant === STORE_FRONT_VARIANTS.standard;      // relies solely on media queries
    const isCondensed = variant === STORE_FRONT_VARIANTS.condensed;    // uses container queries to determine number of columns


    return (
        <StoreFrontContainer.Container
            {...otherProps}
        >
            <HeadingBlock
                {...headingBlockProps}
            />

            <StoreFrontContainer.List
                className={classNames({
                    'is-standard': isStandard,
                    'is-condensed': isCondensed,
                })}
                alignLeft={hasAnyHeading}
                childrenCount={childrenCount}
                columnCount={columnCount}
            >
                {children}
            </StoreFrontContainer.List>
        </StoreFrontContainer.Container>
    );
}

const STORE_FRONT_VARIANTS = {
    standard: 'standard',
    condensed: 'condensed',
};

StoreFrontContainer.propTypes = {
    heading: HeadingPropTypes,
    mainTitle: PropTypes.string,
    variant: PropTypes.oneOf(Object.values(STORE_FRONT_VARIANTS)),
    children: PropTypes.array.isRequired,
};


// The max number of tiles we will try to show in one single row on desktop.
const MAX_TILES_PER_ROW_DESKTOP = 6;

const MAX_TILE_WIDTH_DESKTOP = 336;
const MAX_TILE_WIDTH_CONDENSED = 210;

const BREAKPOINT_LARGE = '60rem'; // matches breakpoint in StoreFrontItem
const BREAKPOINT_SMALL = '40rem'; // matches breakpoint in StoreFrontItem


StoreFrontContainer.Container = styled.div`
    container: store-front-list / inline-size;
`;

StoreFrontContainer.List = styled.ul`
    // 1 column
    display: grid;
    grid-template-columns: 1fr;
    column-gap: 1rem;
    justify-content: center;

    grid-auto-rows: 1fr;

    list-style: none;

    margin: 0;
    padding: 0;

    // land of MEDIA queries
    &.is-standard {
        // Tablet
        @media screen and (min-width: ${BREAKPOINT_SMALL}) and (max-width: ${BREAKPOINT_LARGE}) {
            // Two-col layout, displayed as 4 columns in order to allow centering odd item at the end.
            grid-template-columns: repeat(4, 1fr);

            & > li {
                grid-column: span 2;

                // Center dangling last item by offsetting its grid placement.
                &:nth-last-child(1 of .store-front-item) {
                    &:nth-child(odd of .store-front-item) {
                        grid-column: 2 / span 2;
                    }
                }
            }
        }

        // Desktop
        @media screen and (min-width: ${BREAKPOINT_LARGE}) {
            // Single row.
            ${props => (props.childrenCount <= MAX_TILES_PER_ROW_DESKTOP) && `
                grid-template-columns: repeat(${props.columnCount}, minmax(100px, ${MAX_TILE_WIDTH_DESKTOP}px));
            `}

            // Two rows, with items distributed across the rows as evenly as possible.
            ${props => (props.childrenCount > MAX_TILES_PER_ROW_DESKTOP) && `
                grid-template-columns: repeat(${props.columnCount * 2}, minmax(50px, calc(${MAX_TILE_WIDTH_DESKTOP / 2}px - 0.5rem)));

                & > li {
                    grid-column: span 2;

                    // Centre all last row items by offsetting the first item’s grid placement.
                    ${!props.alignLeft && `
                        &:nth-last-child(${props.columnCount - 1} of .store-front-item) {
                            &:nth-child(${props.columnCount % 2 === 0
        ? 'odd'
        : 'even'} of .store-front-item) {
                                grid-column: 2 / span 2;
                            }
                        }
                    `}
                }
            `}

            // when there is a title, do not centre the last row
            justify-content: ${props => props.alignLeft
        ? 'flex-start'
        : 'center'};
        }
    }


    // variant === condensed -- land of unexplored CONTAINER queries
    /* Displaying the condensed view in the CMS editor appears to have a bug related to
       styled components and container queries. For reasons that remain a mystery, some of
       the @container queries did not have the appropriate styled component’s class name
       included in the stylesheet. Consequently, some of the rules were not being applied
       when they should have been.
       The solution shown below is to duplicate a class name in the selector to increase its
       specificity at particular container widths. This only happens if this styled container
       receives a prop called isCMS that is true and that only happens when called from
       the CMS editor for StoreFrontContainer.
       In addition, the rules in question appear in their own &.is-condensed block. This was
       also necessary (for the same unknown mysterious reasons).
       Note that as of 2025-Apr-28 and SC 6.1.17, it falsely inserts incorrect style rules when
       looping through container queries, at least within the CMS context.
     */
    &.is-condensed {
        gap: ${Sizes.SPACING.ONE_AND_THREE_QUARTERS};

        ${({ alignLeft, childrenCount, columnCount }) => `
            & > li {
                grid-column: span 2;
            }

            // 1 StoreFrontItem columns: 2 grid columns
            @container (width > 50px) {
                & {
                    grid-template-columns: repeat(2, minmax(50px, calc(${MAX_TILE_WIDTH_CONDENSED / 2}px - 0.5rem)));
                }
            }

            // 2 StoreFrontItem columns: 4 grid columns
            @container (width > 450px) {
                & {
                    grid-template-columns: repeat(4, minmax(50px, calc(${MAX_TILE_WIDTH_CONDENSED / 2}px - 0.5rem)));

                    // at least we can identify the last odd item when using 2 columns of items, but expecting
                    // condensed to likely be used only in narrow containers, we only do this when there are 3 children
                    ${!alignLeft && childrenCount === 3 && `
                        @container (width < calc(3 * (${MAX_TILE_WIDTH_CONDENSED}px + 0.5rem))) {
                            & > li:nth-child(3) {
                                    grid-column: 2 / span 2;
                                }
                            }
                        }
                    `};
                }
            }

            // see below for 3 and 4 StoreFrontItem columns

            // 5 StoreFrontItem columns: 10 grid columns
            @container (width > calc(5 * (${MAX_TILE_WIDTH_CONDENSED}px + 0.5rem))) {
                & {
                    grid-template-columns: repeat(${Math.min(10, columnCount * 2)}, minmax(50px, calc(${MAX_TILE_WIDTH_CONDENSED / 2}px - 0.5rem)));
                }
            }

            // 6 StoreFrontItem columns: 12 grid columns
            @container (width > calc(6 * (${MAX_TILE_WIDTH_CONDENSED}px + 0.5rem))) {
                & {
                    grid-template-columns: repeat(${Math.min(12, columnCount * 2)}, minmax(50px, calc(${MAX_TILE_WIDTH_CONDENSED / 2}px - 0.5rem)));
                }
            }
        `};
    }

    &.is-condensed {
        ${({ columnCount, isCMS }) => `
            // 3 StoreFrontItem columns: 6 grid columns
            @container (width > calc(3 * (${MAX_TILE_WIDTH_CONDENSED}px + 0.5rem))) {
                &${isCMS
        ? '&'
        : ''} {
                    grid-template-columns: repeat(${Math.min(6, columnCount * 2)}, minmax(50px, calc(${MAX_TILE_WIDTH_CONDENSED / 2}px - 0.5rem)));
                }
            }

            // 4 StoreFrontItem columns: 8 grid columns
            @container (width > calc(4 * (${MAX_TILE_WIDTH_CONDENSED}px + 0.5rem))) {
                &${isCMS
        ? '&'
        : ''} {
                    grid-template-columns: repeat(${Math.min(8, columnCount * 2)}, minmax(50px, calc(${MAX_TILE_WIDTH_CONDENSED / 2}px - 0.5rem)));
                }
            }
        `};
    }
`;

StoreFrontContainer.blockKey = 'rh/store-front';
StoreFrontContainer.hasHeading = true;
StoreFrontContainer.requiresLayoutRow = true;


export { BREAKPOINT_LARGE, STORE_FRONT_VARIANTS };
export default StoreFrontContainer;
